import { useState } from "react";
import ReferencePicker from "../EmissionFactorBreakdown/ReferencePicker";
import { saveGhgDatabase } from "./ghgDatabasesApi";
import { FACTOR_BASES, MAX_PRIORITY, draftError, draftToPayload } from "./helpers";

/**
 * Adds a GHG database, or edits one, with the publication it is quoted from.
 * The link is a reference from the library, or a new one added here. The
 * draft is checked before it is sent, and the backend's answer is shown in
 * place when it refuses. It is not a <form>: the new-link form sits inside
 * it, and forms cannot nest.
 *
 * @param {object} props
 * @param {import("./helpers").DatabaseDraft} props.initial
 * @param {import("./helpers").GhgDatabase[]} props.databases every database, for the duplicate name check
 * @param {import("../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onReferencesChanged
 * @param {(saved: import("./helpers").GhgDatabase) => void} props.onSaved
 * @param {() => void} props.onCancel
 */
export default function DatabaseForm({ initial, databases, references, onReferencesChanged, onSaved, onCancel }) {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const editing = initial.id != null;
  const renamed = editing && draft.name.trim() !== initial.name;
  const basisLocked = editing && databases.some((database) => database.id === initial.id && database.factor_count > 0);
  const change = (key) => (e) => {
    const { value } = e.target;
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const submit = async () => {
    const problem = draftError(draft, databases);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    const res = await saveGhgDatabase(draft.id, draftToPayload(draft));
    setSaving(false);
    if (res?.isSuccess && res.data?.data) onSaved(res.data.data);
    else setError(res?.data?.error || "Couldn't save the database.");
  };

  return (
    <section className="kgb-card gdb-form" aria-label={editing ? `Edit ${initial.name}` : "Add a GHG database"}>
      <header className="gdb-form-head">
        <h2>{editing ? `Edit ${initial.name}` : "Add a GHG database"}</h2>
        <p>Where emission factors are quoted from. Companies are priced from the database with the lowest priority.</p>
      </header>

      <div className="gdb-form-body">
        <div className="gdb-form-grid">
          <label className="gdb-field gdb-field--wide" htmlFor="gdb-name">
            <span>Name *</span>
            <input id="gdb-name" type="text" maxLength={256} value={draft.name} placeholder="DEFRA 2026" onChange={change("name")} />
            {renamed ? (
              <small className="gdb-warn">The factor import script finds DEFRA 2026 and NAICS 2022 by name.</small>
            ) : null}
          </label>

          <label className="gdb-field gdb-field--wide" htmlFor="gdb-source">
            <span>Source label</span>
            <input
              id="gdb-source"
              type="text"
              maxLength={512}
              value={draft.source}
              placeholder="UK Government GHG Conversion Factors 2026"
              onChange={change("source")}
            />
          </label>

          <label className="gdb-field" htmlFor="gdb-priority">
            <span>Priority</span>
            <input
              id="gdb-priority"
              type="number"
              min="1"
              max={MAX_PRIORITY}
              step="1"
              value={draft.priority}
              placeholder={String(MAX_PRIORITY)}
              onChange={change("priority")}
            />
            <small>1 is used first; blank sorts last.</small>
          </label>

          <fieldset className="gdb-field gdb-basis-picker" disabled={basisLocked}>
            <legend>Factor basis</legend>
            <div className="gdb-segments">
              {FACTOR_BASES.map(({ value, label, hint }) => (
                <label key={value} className={`gdb-segment${draft.basis === value ? " gdb-segment--on" : ""}`}>
                  <input type="radio" name="gdb-basis" value={value} checked={draft.basis === value} onChange={change("basis")} />
                  <strong>{label}</strong>
                  <small>{hint}</small>
                </label>
              ))}
            </div>
            {basisLocked ? <small>Fixed while the database holds factors: the basis decides how companies price them.</small> : null}
          </fieldset>

          {draft.basis === "SPEND" ? (
            <label className="gdb-field" htmlFor="gdb-year">
              <span>Price year *</span>
              <input id="gdb-year" type="number" min="1990" max="2100" value={draft.priceYear} placeholder="2022" onChange={change("priceYear")} />
              <small>The year whose currency the factors are priced in.</small>
            </label>
          ) : null}

          <ReferencePicker
            id="gdb-reference"
            label="Source link"
            value={draft.referenceId}
            references={references}
            onChange={(referenceId) => setDraft((current) => ({ ...current, referenceId }))}
            onReferencesChanged={onReferencesChanged}
          />
        </div>

        {error ? (
          <p className="gdb-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="gdb-form-actions">
          <button type="button" className="kgb-btn kgb-btn--primary" disabled={saving} onClick={submit}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add database"}
          </button>
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}
